import { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { getIntegrationDisplayName, getLinkableContactObjectName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { IntegrationIcon } from '../../../../../../../components/IntegrationIcon'
import { Button } from '../../../../../../../components/Button/Button'
import { SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { DeletableContactLinkRow } from './DeletableContactLinkRow'
import { ContactLinkingSection } from '../../ContactLinking/ContactLinkingSection'
import { contactLinkNodeWithSelectedField, contactLinkNodeWithSelectedObject } from '../../ContactLinking/contactLinkNodes'
import { FormFieldDefinition } from '../../../hooks'
import { IntegrationSettings } from '../../../schema'
import { useGetSchemaErrors } from '../../../schemaErrors'

type CalendarMapping = NonNullable<IntegrationSettings['mappings']['calendar']>
type CalendarContactLink = NonNullable<CalendarMapping['contactLinks']>[number]

const CONTACT_LINKS_PATH = 'mappings.calendar.contactLinks' as const

interface CalendarContactLinkingProps {
  serviceId: IntegrationServiceId
}

/**
 * Calendar links point the other way round from the generic `ContactLinking` component: every link reads a lookup
 * field **on the event object** (`Event.WhoId`, `Meeting__c.Client__c`) and resolves it against the contact object,
 * and an event may carry several of them (`WhoId` and `WhatId`). Hence a dedicated, event-object-anchored editor.
 */
export const CalendarContactLinking: React.FC<CalendarContactLinkingProps> = ({ serviceId }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const { getErrorOnPath } = useGetSchemaErrors()

  const eventObject = watch('mappings.calendar.objectName')
  const contactObjectRaw = watch('mappings.contact.objectName')
  const contactObject = useMemo(() => getLinkableContactObjectName(serviceId, contactObjectRaw ?? null), [serviceId, contactObjectRaw])

  const storedLinks = watch('mappings.calendar.contactLinks')

  const emptyLink: CalendarContactLink = useMemo(
    () => ({ source: { object: eventObject ?? null, field: null }, target: { object: contactObject, field: null } }),
    [eventObject, contactObject],
  )

  const links: CalendarContactLink[] = useMemo(() => (storedLinks?.length ? storedLinks : [emptyLink]), [storedLinks, emptyLink])

  const linkingError = getErrorOnPath(CONTACT_LINKS_PATH)

  const updateLinks = useCallback(
    (updatedLinks: CalendarContactLink[]) => {
      setValue(CONTACT_LINKS_PATH, updatedLinks, { shouldDirty: true, shouldValidate: true })
    },
    [setValue],
  )

  const updateLinkAt = useCallback(
    (index: number, updatedLink: CalendarContactLink) => {
      updateLinks(links.map((link, linkIndex) => (linkIndex === index ? updatedLink : link)))
    },
    [links, updateLinks],
  )

  const onSourceFieldSelected = (index: number, option: SelectOption, fieldDefinition?: FormFieldDefinition) => {
    const link = links[index]
    updateLinkAt(index, { ...link, source: { ...link.source, ...contactLinkNodeWithSelectedField(eventObject ?? null, option, fieldDefinition) } })
  }

  const onTargetObjectSelected = (index: number, option: SelectOption) => {
    const link = links[index]
    updateLinkAt(index, { ...link, target: contactLinkNodeWithSelectedObject(link.target, option.id) })
  }

  const onTargetFieldSelected = (index: number, option: SelectOption, fieldDefinition?: FormFieldDefinition) => {
    const link = links[index]
    updateLinkAt(index, { ...link, target: contactLinkNodeWithSelectedField(link.target?.object ?? contactObject, option, fieldDefinition) })
  }

  const onAddLink = () => {
    updateLinks([...links, emptyLink])
  }

  const onRemoveLink = (index: number) => {
    updateLinks(links.filter((_, linkIndex) => linkIndex !== index))
  }

  const integrationIcon = <IntegrationIcon serviceId={serviceId} width='1rem' height='1rem' fill='black' />

  return (
    <ContactLinkingSection
      description={
        <>
          Pick the {eventObject} fields that point at a contact. Zocks reads the attendees of an event from them, so add one row per lookup — a{' '}
          {getIntegrationDisplayName(serviceId)} Event usually needs both WhoId and WhatId.
        </>
      }
      sourceHeading={{ icon: integrationIcon, label: `${eventObject?.toUpperCase()} FIELD` }}
      targetHeading={{ icon: integrationIcon, label: `${getIntegrationDisplayName(serviceId).toUpperCase()} CONTACT` }}
      hasDeletableRows
      error={linkingError}
      footer={
        <Button type='button' variant='primaryGhost' className='border-0' onClick={onAddLink}>
          + Add another link
        </Button>
      }
    >
      {links.map((link, index) => (
        <DeletableContactLinkRow
          key={`calendar-contact-link-${index}`}
          serviceId={serviceId}
          source={{
            objectName: eventObject ?? null,
            initialSelectedField: link.source?.field ?? null,
            predefinedObjectSelectOptions: eventObject ? [{ id: eventObject, label: eventObject }] : [],
            onObjectSelected: () => {},
            onFieldSelected: (option, fieldDefinition) => onSourceFieldSelected(index, option, fieldDefinition),
            isObjectSelectionDisabled: true,
          }}
          target={{
            objectName: link.target?.object ?? null,
            initialSelectedField: link.target?.field ?? null,
            onObjectSelected: (option) => onTargetObjectSelected(index, option),
            onFieldSelected: (option, fieldDefinition) => onTargetFieldSelected(index, option, fieldDefinition),
          }}
          onDelete={() => onRemoveLink(index)}
          isDeleteDisabled={links.length === 1}
          deleteLabel={`Remove contact link ${index + 1}`}
        />
      ))}
    </ContactLinkingSection>
  )
}
